// Quick-add menu for Sales Activities — FE-SCRM-024, polished in
// FE-SCRM-061 to collapse the four chips + "Log activity" button
// into a single "+ Log activity ▾" dropdown. Mirrors FE-PINV-066's
// NewChooser pattern for ARIA + keyboard behavior so producers get
// the same dropdown UX across the CRM.
//
// The on_pick contract is unchanged: parents receive an
// ``ActivityType`` for the four quick rows and ``null`` for
// "Custom…" (which opens the composer's full type picker).

import {
  useCallback, useEffect, useRef, useState,
} from "react";

import { QUICK_ADD_TYPES, activityTypeLabel } from "./activityTypeMeta";
import type { ActivityType } from "./types";


export function QuickAddActivityMenu({
  on_pick,
}: {
  on_pick: (type: ActivityType | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstRowRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the first menu row when the dropdown opens so
  // keyboard users land on a target immediately.
  useEffect(() => {
    if (open) firstRowRef.current?.focus();
  }, [open]);

  const pick = useCallback(
    (type: ActivityType | null) => {
      setOpen(false);
      on_pick(type);
    },
    [on_pick],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Enter") {
        // happy-dom doesn't always translate Enter-on-button to a
        // click event; dispatch explicitly so the a11y AC holds.
        const focused = document.activeElement;
        if (focused instanceof HTMLElement) {
          e.preventDefault();
          focused.click();
        }
      }
    },
    [],
  );

  return (
    <div
      data-testid="quick-add-activity-menu"
      className="relative inline-block"
    >
      <button
        ref={triggerRef}
        type="button"
        data-testid="quick-add-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1 text-xs font-medium text-white hover:opacity-90"
      >
        + Log activity
        <span aria-hidden="true">▾</span>
      </button>

      {open ? (
        <div
          data-testid="quick-add-dropdown"
          role="menu"
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="absolute right-0 top-full z-30 mt-1 w-56 rounded-md border border-border-default bg-raised shadow-overlay"
        >
          {QUICK_ADD_TYPES.map((t, i) => (
            <button
              key={t}
              ref={i === 0 ? firstRowRef : undefined}
              type="button"
              role="menuitem"
              data-testid={`quick-add-row-${t}`}
              onClick={() => pick(t)}
              className="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-recessed"
            >
              {activityTypeLabel(t)}
            </button>
          ))}
          <div
            role="separator"
            aria-hidden="true"
            className="my-1 border-t border-border-subtle"
          />
          <button
            type="button"
            role="menuitem"
            data-testid="quick-add-row-custom"
            onClick={() => pick(null)}
            className="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-recessed"
          >
            Custom…
          </button>
        </div>
      ) : null}
    </div>
  );
}
